import { useCallback, useEffect, useState } from 'react';
import { isTextItemVisibleOnCanvas } from '../model/textItems';

export default function useMoveHistory({ textItems, textItemsRef, setTextItems, textNodes, setTextNodes }) {
  const [history, setHistory] = useState([]);

  const recordMove = useCallback((kind, itemId, previous, next) => {
    if (Object.keys(next).every((key) => previous[key] === next[key])) return;
    setHistory((moves) => [...moves, { kind, itemId, previous }]);
  }, []);

  const undoMove = useCallback(() => {
    const lastMove = history[history.length - 1];
    if (!lastMove) return;

    if (lastMove.kind === 'text') {
      const nextItems = textItemsRef.current.map((item) => {
        if (item.id !== lastMove.itemId) return item;
        const style = { ...(item.style || {}) };
        if (lastMove.previous.positionMode === undefined) delete style.positionMode;
        else style.positionMode = lastMove.previous.positionMode;
        return {
          ...item,
          position: { ...(item.position || {}), x: lastMove.previous.x, y: lastMove.previous.y },
          style,
          ...(item.schemaVersion === 1 ? { layoutConfig: { ...item.layoutConfig, positionMode: lastMove.previous.positionMode || 'custom' } } : {}),
        };
      });
      textItemsRef.current = nextItems;
      setTextItems(nextItems);
    } else {
      setTextNodes((nodes) => nodes.map((node) => node.id === lastMove.itemId
        ? { ...node, ...lastMove.previous }
        : node));
    }
    setHistory((moves) => moves.slice(0, -1));
  }, [history, setTextItems, setTextNodes, textItemsRef]);

  useEffect(() => {
    const textIds = new Set(textItems.filter(isTextItemVisibleOnCanvas).map((item) => item.id));
    const artworkIds = new Set(textNodes.map((node) => node.id));
    setHistory((moves) => {
      const remaining = moves.filter(({ kind, itemId }) => kind === 'text'
        ? textIds.has(itemId)
        : artworkIds.has(itemId));
      return remaining.length === moves.length ? moves : remaining;
    });
  }, [textItems, textNodes]);

  const clearMoveHistory = useCallback(() => setHistory([]), []);

  return { canUndoMove: history.length > 0, clearMoveHistory, recordMove, undoMove };
}
